// Minimal, dependency-free markdown renderer for chat message display
// (PreviewPlayer's Test panel; the vanilla-JS live embed has its own
// parallel port in public/embed/flow-embed.js — see that file's comment
// for why it isn't shared code). Deliberately builds real React elements,
// never dangerouslySetInnerHTML — matches this codebase's established
// "no HTML-rendering sink" security posture (see src/utils/security.js).
// Covers the subset chat messages actually use: headers, bold, italic,
// inline code, and lists. Not a full CommonMark implementation.

const INLINE_PATTERN = /(`[^`]+`)|(\*\*[^*]+\*\*)|(__[^_]+__)|(\*[^*]+\*)|(_[^_]+_)/g;

// Exported (alongside parseMarkdownBlocks) purely so this parsing logic is
// unit-testable without a DOM/JSX renderer — renderMarkdown itself is kept
// a thin, untested wrapper over these two.
export function parseInlineMarkdown(text) {
  const segments = [];
  let lastIndex = 0;
  let match;
  while ((match = INLINE_PATTERN.exec(text)) !== null) {
    if (match.index > lastIndex) segments.push({ type: 'text', text: text.slice(lastIndex, match.index) });
    const token = match[0];
    if (token.startsWith('`')) segments.push({ type: 'code', text: token.slice(1, -1) });
    else if (token.startsWith('**') || token.startsWith('__')) segments.push({ type: 'bold', text: token.slice(2, -2) });
    else segments.push({ type: 'italic', text: token.slice(1, -1) });
    lastIndex = INLINE_PATTERN.lastIndex;
  }
  if (lastIndex < text.length) segments.push({ type: 'text', text: text.slice(lastIndex) });
  return segments;
}

export function parseMarkdownBlocks(text) {
  const lines = String(text ?? '').split('\n');
  const blocks = [];
  let listItems = null;
  let listType = null;
  const flushList = () => {
    if (listItems) blocks.push({ type: listType, items: listItems });
    listItems = null;
    listType = null;
  };
  for (const line of lines) {
    const headingMatch = line.match(/^(#{1,6})\s+(.*)$/);
    const ulMatch = line.match(/^[-*]\s+(.*)$/);
    const olMatch = line.match(/^\d+\.\s+(.*)$/);
    if (headingMatch) {
      flushList();
      blocks.push({ type: 'heading', level: headingMatch[1].length, text: headingMatch[2] });
    } else if (ulMatch) {
      if (listType !== 'ul') flushList();
      listType = 'ul';
      (listItems || (listItems = [])).push(ulMatch[1]);
    } else if (olMatch) {
      if (listType !== 'ol') flushList();
      listType = 'ol';
      (listItems || (listItems = [])).push(olMatch[1]);
    } else {
      flushList();
      blocks.push({ type: 'paragraph', text: line });
    }
  }
  flushList();
  return blocks;
}

const HEADING_SIZES = ['text-xl', 'text-lg', 'text-base', 'text-base', 'text-sm', 'text-sm'];

function renderInline(text, keyPrefix) {
  return parseInlineMarkdown(text).map((seg, i) => {
    const key = `${keyPrefix}-${i}`;
    if (seg.type === 'bold') return <strong key={key}>{seg.text}</strong>;
    if (seg.type === 'italic') return <em key={key}>{seg.text}</em>;
    if (seg.type === 'code') return <code key={key} className="px-1 py-0.5 rounded bg-black/5 font-mono text-[0.9em]">{seg.text}</code>;
    return seg.text;
  });
}

export function renderMarkdown(text) {
  return parseMarkdownBlocks(text).map((block, i) => {
    const key = `b${i}`;
    if (block.type === 'heading') {
      return (
        <div key={key} className={`font-semibold ${HEADING_SIZES[block.level - 1]} mt-1 first:mt-0`}>
          {renderInline(block.text, key)}
        </div>
      );
    }
    if (block.type === 'ul') {
      return (
        <ul key={key} className="list-disc pl-5">
          {block.items.map((item, j) => <li key={j}>{renderInline(item, `${key}-${j}`)}</li>)}
        </ul>
      );
    }
    if (block.type === 'ol') {
      return (
        <ol key={key} className="list-decimal pl-5">
          {block.items.map((item, j) => <li key={j}>{renderInline(item, `${key}-${j}`)}</li>)}
        </ol>
      );
    }
    if (!block.text.trim()) return <div key={key} className="h-2" />;
    return <div key={key}>{renderInline(block.text, key)}</div>;
  });
}
